import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../../redux/store";
import { getCCOsNeedAttentionByOrgId } from "../../redux/thunks/ccoAdvanceTab2Thunk";
import { toggleModal } from "../../redux/slices/modalSlice";
import { setConnection } from "../../redux/slices/certConnectSlice";
import { Box, Typography, Chip, useMediaQuery, useTheme, Button, TextField, InputAdornment } from "@mui/material";
import iconImage from "../../assets/images/user-image.png";
import "../../sass/base/ccoAdvance.scss";
import CustomColumnMenu from "../../components/MyCCOPro/CustomColumnMenu";
import { DataGrid, GridColDef, GridSortModel, GridOverlay, GridColumnVisibilityModel, } from "@mui/x-data-grid";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { FaSearch } from "react-icons/fa";
import HeaderWithFilterIcon from "../../components/MyCCOPro/HeaderWithFilterIcon";
import React from "react";
import CertConnectCustomFilterModal, { FilterItem } from "../../components/Modals/CertConnectCustomFilterModal";
import { t } from "i18next";
import ListViewNeedAttention from "./ListViewNeedAttention";

const ACTION_REQUIRED_LABELS: Record<number, string> = {
  1: t("dataGrid.accountLocked"),
  2: t("dataGrid.pendingConnectionApproval"),
  3: t("dataGrid.noCertifications"),
  4: t("dataGrid.missingPhoto"),
};
const ACTION_REQUIRED_CODE_BY_LABEL: Record<string, number> = {
  "Account Locked": 1,
  "Pending Connection Approval": 2,
  "No Certifications": 3,
  "Missing Photo": 4,
};

const getActionRequired = (actionRequired: number | null) => {
  if (actionRequired === null || actionRequired === undefined) return null;

  const label = ACTION_REQUIRED_LABELS[actionRequired] ?? String(actionRequired);

  if (actionRequired === 1) {
    return { label: t("dataGrid.accountLocked"), color: "#fff", backgroundColor: "#ff0000" };
  } else if (actionRequired === 2) {
    return { label: t("dataGrid.pendingConnectionApproval"), color: "#000000", backgroundColor: "#ffff00" };
  } else if (actionRequired === 3) {
    return { label: t("dataGrid.noCertifications"), color: "#fff", backgroundColor: "#9e9e9e" };
  } else if (actionRequired === 4) {
    return { label: t("dataGrid.missingPhoto"), color: "#fff", backgroundColor: "#ff0000" };
  }

  return { label, color: "#000", backgroundColor: "#eee" };
};

const CustomNoRowsOverlay = () => (
  <GridOverlay>
    <div style={{ padding: 16, color: "#555" }}>{t("dataGrid.NoRowsLabel")}</div>
  </GridOverlay>
);
interface Props {
  activeTab: number;
}

const getActionRequiredValue = (v: unknown): number | null => {
  if (v === null || v === undefined) return null;

  if (typeof v === "number") return Number.isFinite(v) ? v : null;

  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return null;

    const mapped = ACTION_REQUIRED_CODE_BY_LABEL[s];
    if (mapped) return mapped;

    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }

  const n = Number(v as any);
  return Number.isFinite(n) ? n : null;
};

const DataGridNeedAtttentions: React.FC<Props> = ({ activeTab = 0 }) => {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'LastName', sort: 'asc' }]);
  const [customFilters, setCustomFilters] = useState<FilterItem[]>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [clearFilter, setClearFilter] = useState(false);

  const [filters, setFilters] = useState({
    firstName: "",
    lastName: "",
    ccoId: "",
    statusFilter: null as number | null,
  });

  const dispatch = useDispatch<AppDispatch>();
  const { organizationId } = useParams<{ organizationId: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { ccoLists, Loading, totalNeedAttentionCount } = useSelector((state: RootState) => state.ccoAdvanceTab2);
  const { activeDesignations } = useSelector((state: RootState) => state.designation);

  const rows = ccoLists;
  const totalCount = totalNeedAttentionCount;
  const loading = Loading;

  const hasHiddenColumns = useMemo(() => {
    return Object.values(columnVisibilityModel).some(value => value === false);
  }, [columnVisibilityModel]);

  const handleResetColumns = () => {
    setColumnVisibilityModel({});
  };

  const hasActiveFiltersOrSort = useMemo(() => {
    const hasFilters = customFilters.length > 0;
    const currentSort = sortModel[0] || { field: 'LastName', sort: 'asc' };
    const isDefaultSort = currentSort.field === 'LastName' && currentSort.sort === 'asc';
    const hasSorting = !isDefaultSort;
    const hasSearch = searchTerm.trim() !== "";
    return hasFilters || hasSorting || hasSearch;
  }, [customFilters, sortModel, searchTerm]);

  const handleResetFiltersAndSort = useCallback(() => {
    setCustomFilters([]);
    setColumnVisibilityModel({});
    setSearchTerm("");
    setFilters({
      firstName: "",
      lastName: "",
      ccoId: "",
      statusFilter: null,
    });
    setSortModel([{ field: 'LastName', sort: 'asc' }]);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setClearFilter(prev => !prev);
  }, []);

  const gridRows = useMemo(() => {
    return rows.map((r, i) => ({
      ...r,
      uniqueId: `${r.ContactId || 'unknown'}-${i}`
    }));
  }, [rows]);

  useEffect(() => {
    if (clearFilter) {
      setCustomFilters([]);
      setSearchTerm("");
      setFilters({
        firstName: "",
        lastName: "",
        ccoId: "",
        statusFilter: null,
      });
      setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    }
  }, [clearFilter]);

  useEffect(() => {
    setSearchTerm("");
    setCustomFilters([]);
    setFilters({
      firstName: "",
      lastName: "",
      ccoId: "",
      statusFilter: null,
    });
    setPaginationModel({ page: 0, pageSize: 25 });
    setSortModel([{ field: 'LastName', sort: 'asc' }]);
  }, [activeTab]);

  useEffect(() => {
    if (!organizationId) return;

    const timer = setTimeout(() => {
      const currentSort = sortModel[0] || { field: 'LastName', sort: 'asc' };

      const apiParams = {
        organizationId,
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        page: paginationModel.page + 1,
        sortBy: currentSort.field,
        sortOrder: (currentSort.sort === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc',
        searchTerm: searchTerm.trim() || undefined,
        FirstNameSearch: filters.firstName || "",
        LastNameSearch: filters.lastName || "",
        CcoIdSearchTermTab2: filters.ccoId || "",
        statusFilter: filters.statusFilter ?? null,
      };
      dispatch(getCCOsNeedAttentionByOrgId(apiParams));
    }, 300);

    return () => clearTimeout(timer);
  }, [
    dispatch,
    organizationId,
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
    filters,
  ]);


  const handleSearch = useCallback(() => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    if (!organizationId) return;

    const currentSort = sortModel[0] || { field: 'LastName', sort: 'asc' };

    const apiParams = {
      organizationId,
      pageNumber: 1,
      pageSize: paginationModel.pageSize,
      page: 1,
      sortBy: currentSort.field,
      sortOrder: (currentSort.sort === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc',
      searchTerm: searchTerm.trim() || undefined,
      FirstNameSearch: filters.firstName || "",
      LastNameSearch: filters.lastName || "",
      CcoIdSearchTermTab2: filters.ccoId || "",
      statusFilter: filters.statusFilter ?? null,
    };

    dispatch(getCCOsNeedAttentionByOrgId(apiParams));
  }, [dispatch, organizationId, paginationModel.pageSize, sortModel, searchTerm, filters]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model.length > 0 ? model : [{ field: 'LastName', sort: 'asc' }]);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    },
    []
  );

  const handleFilterChange = useCallback(
    (filterItems: FilterItem[]) => {
      if (JSON.stringify(filterItems) === JSON.stringify(customFilters)) return;
      setCustomFilters(filterItems);

      const newFilters = {
        firstName: "",
        lastName: "",
        ccoId: "",
        statusFilter: null as number | null,
      };

      filterItems.forEach((filter) => {
        if (!filter.value && filter.value !== false && filter.value !== 0) return;

        switch (filter.field) {
          case "FirstName":
            newFilters.firstName = String(filter.value);
            break;
          case "LastName":
            newFilters.lastName = String(filter.value);
            break;
          case "ContactCCOID":
            newFilters.ccoId = String(filter.value);
            break;
          case "ActionRequired":
            newFilters.statusFilter = filter.value ? Number(filter.value) : null;
            break;
        }
      });
      setFilters(newFilters);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    },
    [customFilters]
  );


  const columns: GridColDef[] = useMemo(() => {
    const common: GridColDef[] = [
      {
        field: "FirstName",
        headerName: t("dataGrid.FirstName"),
        headerClassName: "custom-header",
        width: 180,
        filterable: true,
        renderHeader: (params) => (
          <HeaderWithFilterIcon params={params} filters={customFilters} name={t("dataGrid.FirstName")} />
        ),

        renderCell: (params) => (
          <div className="profile-cell">
            <img
              src={params.row.ContactProfilePic || iconImage}
              alt="profile"/>
            <span> {params.value} </span>
          </div>
        ),
      },

      {
        field: "MiddleName",
        headerClassName: "custom-header",
        headerName: t("dataGrid.MI"),
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <span  className="render-cell">{params.value || "----"}</span>
        ),
      },
      {
        field: "LastName",
        headerName: t("dataGrid.LastName"),
        headerClassName: "custom-header",
        width: 170,
        filterable: true,
        renderCell: (params) => (
          <span  className="render-cell">{params.value}</span>
        ),
        renderHeader: (params) => (
          <HeaderWithFilterIcon params={params} filters={customFilters} name={t("dataGrid.LastName")} />
        ),
      },
      {
        field: "ContactCCOID",
        headerClassName: "custom-header",
        headerName: t("dataGrid.CCOID"),
        width: 140,
        filterable: true,
        renderHeader: (params) => (
          <HeaderWithFilterIcon params={params} filters={customFilters} name={t("dataGrid.CCOID")} />
        ),
      },
    ];


    return [
      ...common,
      {
        field: "ActionRequired",
        headerName: t("dataGrid.ActionRequired"),
        headerClassName: "custom-header",
        sortable: false,
        flex: 1,
        type: 'singleSelect',
        valueOptions: [
          { value: 1, label: "Account Locked" },
          { value: 2, label: "Pending Connection Approval" },
          { value: 3, label: "No Certifications" },
          { value: 4, label: "Missing Photo" }
        ],
        renderCell: (params) => {
          const action = getActionRequired(getActionRequiredValue(params.value));
          if (!action) return "—";
          return (
            <Chip
              label={action.label}
              size="small"
              sx={{
                backgroundColor: action.backgroundColor,
                color: action.color,
                fontWeight: 600,
                borderRadius: "4px",
                height: "24px",
              }}
            />
          );
        },
        renderHeader: (params) => (
          <HeaderWithFilterIcon params={params} filters={customFilters} name={t("dataGrid.ActionRequired")} />
        ),
      }
    ];

  }, [activeDesignations, customFilters]);

  const handleClick = (row: any) => {
    dispatch(
      setConnection({
        ...row,
        ContactProfilePic: row.ContactProfilePic || "",
        ContactCCOID: row.ContactCCOID || "",
        ContactName: row.FirstName + " " + row.LastName,
      })
    );
    dispatch(toggleModal("connectionModal"));
  };
  if (isMobile) {
    return (
      <ListViewNeedAttention
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleSearch={handleSearch}
        gridRows={gridRows}
        handleClick={handleClick}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        totalCount={totalCount}
        onSearch={handleSearch}
        onRowClick={handleClick}
      />
    );
  }
  return (
    <Box className="main-box">
      <Box className="main-box__header">
        <Box className="header-content">
          <CertConnectCustomFilterModal
            filters={customFilters}
            onFiltersChange={handleFilterChange}
            isTab1={false}
            activeDesignations={activeDesignations}
            clearFilter={clearFilter}
          />

          <Typography variant="body2" color="textSecondary">
            {customFilters.length > 0 ? `${t("dataGrid.FilterApplied", { count: customFilters.length })}` : t("dataGrid.NoFilters")}
          </Typography>
          <Typography className="typography" >
            {t("dataGrid.ResultsCount", { count: totalCount })}
          </Typography>
        </Box>

        <Box className="header-inner-content">
          {(hasActiveFiltersOrSort || hasHiddenColumns) && (
            <Button
              startIcon={<RestartAltIcon />}
              onClick={handleResetFiltersAndSort}
              size="small"
              variant="outlined"
              className="btn-reset" >
              {t("dataGrid.Reset")}
            </Button>
          )}

          <TextField
            size="small"
            placeholder={t("dataGrid.SearcboxNeed")}
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
           className="text-field"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FaSearch
                    className="search-icon clickable"
                    onClick={handleSearch}
                  />
                </InputAdornment>
              ),
            }}
          />

        </Box>
      </Box>
      <div className="data-grid-wrapper">
        <DataGrid
          rows={gridRows}
          columns={columns}
          rowCount={totalCount}
          loading={loading}
          getRowId={(row) => row.uniqueId}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
          autoHeight
          disableRowSelectionOnClick
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            columnMenu: CustomColumnMenu,
          }}
          onRowClick={(params) => {
            dispatch(
              setConnection({
                ...params.row,
                ContactProfilePic: params.row.ContactProfilePic || "",
                ContactCCOID: params.row.ContactCCOID || "",
                ContactName:
                  params.row.ContactName || `${params.row.FirstName} ${params.row.LastName} `,
              })
            );
            dispatch(toggleModal("connectionModal"));
          }}
          localeText={{ paginationRowsPerPage: t("dataGrid.RowsPerPage") }}
        />
      </div>
    </Box>
  );
};

export default DataGridNeedAtttentions;
